/**
 * @file ActivityComposerDialog.tsx
 * @description Форма «+ Заметка / + Задача / + Звонок» (phase19.31, SC61,
 * SC68, SC60): открывается окном (на телефоне — нижней шторкой) и не
 * занимает экран, вид выбран кнопкой — второго выбора вида в форме нет.
 *
 * Привязка — `target`: в карточке сделки клиент и сделка, в карточке клиента
 * только клиент (задача и заметка о клиенте, SC68). Без `target` (страница
 * «Задачи») клиента выбирают в форме — выбор обязателен.
 *
 * Поля: заголовок (обязателен), подробности, срок и исполнитель (только у
 * задачи, звонка и встречи; исполнитель по умолчанию — текущий сотрудник,
 * crm.md T1). Кнопка «Добавить» стандартной ширины. Отказ сервера по
 * клиенту или исполнителю — под полем; прочие — уведомлением человеческим
 * текстом, форма остаётся.
 */

import { useState } from "react";

import { ACTIVITY_KIND_META, buildDueAt } from "./activity-kinds";
import { CrmDialogSurface } from "../CrmDialogSurface";
import { TaskAssigneeField } from "../tasks/TaskAssigneeField";
import { TaskCustomerField } from "../tasks/TaskCustomerField";
import { Button } from "../../ui/button";
import { DateField } from "../../ui/DateField";
import { DialogFooter } from "../../ui/dialog";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { useNotify } from "../../ui/NotifyProvider";
import { Textarea } from "../../ui/textarea";
import { ApiError } from "../../../lib/api-client";
import { createActivity, type ActivityKind } from "../../../lib/api-crm";
import type { CustomerDto } from "../../../lib/api-customers";

/** К кому привязать запись: клиент обязателен, сделка — если форма из сделки. */
export interface ActivityTarget {
  customerId: string;
  dealId?: string;
}

interface Props {
  kind: ActivityKind;
  /** Нет — клиента выбирают в форме (страница «Задачи»). */
  target?: ActivityTarget;
  onClose: () => void;
  onSaved: () => void;
}

/** Отказы сервера, которые показываем под полем, а не уведомлением. */
const FIELD_ERRORS: Record<string, { field: "customer" | "assignee"; text: string }> = {
  activity_customer_required: { field: "customer", text: "Выберите клиента — задача всегда о клиенте" },
  customer_not_found: { field: "customer", text: "Клиент не найден — выберите другого" },
  activity_assignee_invalid: {
    field: "assignee",
    text: "Этого сотрудника нельзя назначить: у него нет доступа к клиентам и сделкам",
  },
};

type FieldErrors = Partial<Record<"title" | "customer" | "assignee", string>>;

export function ActivityComposerDialog({ kind, target, onClose, onSaved }: Props) {
  const notify = useNotify();
  const meta = ACTIVITY_KIND_META[kind];
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [dueTime, setDueTime] = useState("");
  const [customer, setCustomer] = useState<CustomerDto | null>(null);
  const [assigneeId, setAssigneeId] = useState("");
  const [errors, setErrors] = useState<FieldErrors>({});
  const [saving, setSaving] = useState(false);

  const customerId = target?.customerId ?? customer?.id;

  async function submit() {
    const found: FieldErrors = {};
    if (!title.trim()) found.title = "Напишите, о чём это — одной фразой";
    if (!customerId) found.customer = "Выберите клиента — задача всегда о клиенте";
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    setSaving(true);
    try {
      await createActivity({
        customerId,
        dealId: target?.dealId,
        kind,
        title: title.trim(),
        content: content.trim() || undefined,
        dueAt: meta.withDue ? buildDueAt(dueDate, dueTime) : undefined,
        assigneeUserId: meta.assignable && assigneeId ? assigneeId : undefined,
      });
      notify.success(meta.addedText);
      onSaved();
    } catch (err) {
      const fieldError = err instanceof ApiError ? FIELD_ERRORS[err.code] : undefined;
      if (fieldError) setErrors({ [fieldError.field]: fieldError.text });
      else notify.error(err instanceof ApiError ? err.message : "Не удалось добавить. Попробуйте ещё раз");
    } finally {
      setSaving(false);
    }
  }

  return (
    <CrmDialogSurface title={meta.dialogTitle} onClose={onClose}>
      <form
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="activity-title">Заголовок</Label>
          <Input
            id="activity-title"
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              setErrors((prev) => ({ ...prev, title: undefined }));
            }}
            placeholder={meta.placeholder}
            aria-invalid={errors.title ? true : undefined}
            aria-describedby={errors.title ? "activity-title-error" : undefined}
            autoFocus
          />
          {errors.title ? (
            <p id="activity-title-error" className="type-caption text-[var(--status-error-fg)]">
              {errors.title}
            </p>
          ) : null}
        </div>
        {target ? null : (
          <TaskCustomerField
            customer={customer}
            onChange={(c) => {
              setCustomer(c);
              setErrors((prev) => ({ ...prev, customer: undefined }));
            }}
            error={errors.customer}
          />
        )}
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="activity-content">Подробности (необязательно)</Label>
          <Textarea
            id="activity-content"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows={3}
            placeholder="Детали, договорённости"
          />
        </div>
        {meta.withDue ? (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="activity-due">Срок (необязательно)</Label>
            <div className="flex gap-2">
              <DateField id="activity-due" value={dueDate} onChange={setDueDate} title="Срок" className="min-w-0 flex-1" />
              <Input
                type="time"
                aria-label="Время"
                value={dueTime}
                onChange={(e) => setDueTime(e.target.value)}
                disabled={!dueDate}
                className="w-[120px]"
              />
            </div>
          </div>
        ) : null}
        {meta.assignable ? (
          <TaskAssigneeField
            value={assigneeId}
            onChange={(id) => {
              setAssigneeId(id);
              setErrors((prev) => ({ ...prev, assignee: undefined }));
            }}
            error={errors.assignee}
          />
        ) : null}
        {/* Кнопки справа, стандартной ширины — и в окне, и в шторке. */}
        <DialogFooter className="flex-row justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Отмена
          </Button>
          <Button type="submit" loading={saving}>
            Добавить
          </Button>
        </DialogFooter>
      </form>
    </CrmDialogSurface>
  );
}
